import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  CalendarClock,
  Megaphone,
  Inbox,
  Settings,
  ShieldCheck,
  UserCircle2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Rocket,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import type { Role } from '@isoft-people/types';

/**
 * WelcomeWizard
 * Tour de bienvenida paso a paso. Se muestra automáticamente la primera vez
 * que un colaborador entra (marca localStorage por usuario) y puede reabrirse
 * desde el menú de usuario disparando el evento `isoft:open-wizard`.
 */

type Step = {
  icon: any;
  title: string;
  body: string;
  accent: string; // clase de color del icono
  bg: string; // fondo del badge del icono
  cta?: { label: string; to: string };
  roles: Role[];
};

const STEPS: Step[] = [
  {
    icon: Sparkles,
    title: 'Bienvenido a ISOFT People',
    body: 'Tu plataforma para gestionar colaboradores, permisos, vacaciones y comunicación interna — todo en un solo lugar. Te mostramos lo esencial en menos de un minuto.',
    accent: 'text-white',
    bg: 'brand-gradient',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
  },
  {
    icon: Home,
    title: 'Tu Inicio',
    body: 'El panel principal te muestra el resumen de tu equipo: headcount, ausencias del día, solicitudes pendientes y colaboradores por país. Es tu punto de partida cada mañana.',
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    cta: { label: 'Ir al inicio', to: '/' },
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
  },
  {
    icon: Users,
    title: 'Colaboradores',
    body: 'Consulta el directorio completo, abre el perfil de cada persona, revisa documentos, desempeño y cumplimiento. Desde aquí también das de alta nuevos colaboradores.',
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    cta: { label: 'Ver colaboradores', to: '/colaboradores' },
    roles: ['ADMIN', 'HR', 'MANAGER'],
  },
  {
    icon: CalendarClock,
    title: 'Permisos y Vacaciones',
    body: 'Solicita permisos o vacaciones, revisa tu saldo de días y consulta el estado de tus solicitudes. Todo queda registrado y notificado automáticamente.',
    accent: 'text-accent',
    bg: 'bg-accent-light',
    cta: { label: 'Ir a permisos', to: '/permisos' },
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
  },
  {
    icon: Inbox,
    title: 'Bandeja de aprobaciones',
    body: 'Como líder, aquí apruebas o rechazas las solicitudes de tu equipo con un clic. Las pendientes aparecen resaltadas para que nada se quede esperando.',
    accent: 'text-warning',
    bg: 'bg-yellow-50',
    cta: { label: 'Abrir bandeja', to: '/bandeja' },
    roles: ['ADMIN', 'HR', 'MANAGER'],
  },
  {
    icon: Megaphone,
    title: 'Anuncios',
    body: 'Mantente al día con los comunicados de la empresa. Recibirás un indicador cuando haya anuncios sin leer dirigidos a tu país o departamento.',
    accent: 'text-info',
    bg: 'bg-blue-50',
    cta: { label: 'Ver anuncios', to: '/anuncios' },
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
  },
  {
    icon: Settings,
    title: 'Configuración',
    body: 'Administra países, catálogos, roles y políticas. Aquí también puedes reiniciar la contraseña de un colaborador y entregarle una temporal de forma segura.',
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    cta: { label: 'Abrir configuración', to: '/configuracion' },
    roles: ['ADMIN', 'HR'],
  },
  {
    icon: ShieldCheck,
    title: 'Auditoría',
    body: 'Cada acción sensible queda registrada: quién hizo qué y cuándo. Como administrador puedes revisar la bitácora completa para total trazabilidad.',
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    cta: { label: 'Ver auditoría', to: '/auditoria' },
    roles: ['ADMIN'],
  },
  {
    icon: UserCircle2,
    title: 'Tu perfil',
    body: 'Desde el menú de tu avatar (arriba a la derecha) accedes a tu perfil y puedes cambiar tu contraseña cuando quieras. ¡Listo para empezar!',
    accent: 'text-accent',
    bg: 'bg-accent-light',
    cta: { label: 'Ver mi perfil', to: '/mi-perfil' },
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
  },
];

export function WelcomeWizard() {
  const user = useAuthStore((s) => s.user);
  const role = (user?.role ?? 'EMPLOYEE') as Role;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const steps = STEPS.filter((s) => s.roles.includes(role));
  const storageKey = user ? `isoft-wizard-seen-${user.id}` : 'isoft-wizard-seen';

  // Auto-abrir la primera vez
  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      // pequeño delay para que el dashboard ya esté pintado
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user, storageKey]);

  // Permitir reabrir desde otros componentes
  useEffect(() => {
    const handler = () => {
      setI(0);
      setOpen(true);
    };
    window.addEventListener('isoft:open-wizard', handler);
    return () => window.removeEventListener('isoft:open-wizard', handler);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    if (user) localStorage.setItem(storageKey, '1');
  }, [user, storageKey]);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight' && i < steps.length - 1) setI((v) => v + 1);
      if (e.key === 'ArrowLeft' && i > 0) setI((v) => v - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, i, steps.length, close]);

  if (!open || steps.length === 0) return null;

  const step = steps[Math.min(i, steps.length - 1)];
  const isFirst = i === 0;
  const isLast = i === steps.length - 1;
  const Icon = step.icon;

  const goTo = (to: string) => {
    close();
    navigate(to);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 anim-fade-in">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-font-primary/50 backdrop-blur-sm"
        onClick={close}
      />

      {/* Card */}
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-hidden overflow-y-auto rounded-2xl bg-white shadow-2xl anim-pop">
        {/* Cabecera con gradiente de marca */}
        <div className="brand-gradient relative px-7 pb-10 pt-7 text-white">
          <button
            onClick={close}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white/90 transition hover:bg-white/25"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
            <Rocket size={14} /> Guía rápida
          </div>
          <div className="mt-5 flex justify-center">
            <div
              className={`flex h-20 w-20 items-center justify-center rounded-2xl ${
                isFirst ? 'bg-white/15 anim-float' : 'bg-white/15'
              } shadow-lg`}
            >
              <Icon size={38} className="text-white" strokeWidth={1.8} />
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="-mt-5 rounded-t-2xl bg-white px-7 pb-7 pt-6">
          <div key={i} className="anim-fade-up">
            <h2 className="text-center text-xl font-bold text-font-primary">
              {step.title}
            </h2>
            <p className="mx-auto mt-3 max-w-md text-center text-sm leading-relaxed text-font-secondary">
              {step.body}
            </p>

            {step.cta && (
              <div className="mt-5 flex justify-center">
                <button
                  onClick={() => goTo(step.cta!.to)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary-subtle px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white"
                >
                  {step.cta.label} <ArrowRight size={15} />
                </button>
              </div>
            )}
          </div>

          {/* Dots */}
          <div className="mt-7 flex items-center justify-center gap-1.5">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === i
                    ? 'w-6 bg-primary'
                    : 'w-2 bg-border hover:bg-primary-light'
                }`}
                aria-label={`Paso ${idx + 1}`}
              />
            ))}
          </div>

          {/* Controles */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={() => (isFirst ? close() : setI((v) => v - 1))}
              className="inline-flex h-11 items-center gap-1.5 rounded-lg px-4 text-sm font-semibold text-font-secondary transition hover:bg-bg"
            >
              {isFirst ? (
                'Saltar'
              ) : (
                <>
                  <ArrowLeft size={15} /> Anterior
                </>
              )}
            </button>

            <span className="text-xs font-medium text-font-tertiary">
              {i + 1} / {steps.length}
            </span>

            <button
              onClick={() => (isLast ? close() : setI((v) => v + 1))}
              className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-primary px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark"
            >
              {isLast ? (
                <>
                  <Check size={16} /> ¡Empezar!
                </>
              ) : (
                <>
                  Siguiente <ArrowRight size={15} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helper para abrir el wizard desde cualquier parte */
export function openWelcomeWizard() {
  window.dispatchEvent(new Event('isoft:open-wizard'));
}
